import type { FieldDef, SectionDef } from '../types';
import {
  sellerOptions,
} from './mockOptions';


export const requestTypeField: FieldDef = {
  key: 'tipoSolicitacao',
  label: 'Tipo de solicitação',
  type: 'select',
  options: [
    { value: '0', label: '0 / Vazio — Promoção nova' },
    { value: '1', label: '1 — Legado (existe na IFC)' },
  ],
  helperText: 'Indica se é promoção nova ou já existente na IFC.',
};

export const idIFCField: FieldDef = {
  key: 'idIFC',
  label: 'ID Infracommerce',
  type: 'text',
  placeholder: 'ex: 123 ou n/a',
  helperText: 'Obrigatório para promoções existentes. Para novas sem correspondência, preencher "n/a".',
};

export const mechanicTypeField = (value: string): FieldDef => ({
  key: 'tipoMecanica',
  label: 'Tipo de mecânica promocional',
  type: 'info',
  helperText: value,
});

export const promoNameField: FieldDef = {
  key: 'nomePromo',
  label: 'Nome da promo / cupom',
  type: 'text',
  required: true,
  placeholder: 'Nome interno da promoção',
  helperText: 'Nome interno. Deve ser idêntico em todos os cards de uma mesma promoção.',
};

export const displayNameField: FieldDef = {
  key: 'nomeExibicao',
  label: 'Nome de exibição da promoção',
  type: 'text',
  required: true,
  placeholder: 'Nome exibido no site e app',
  helperText: 'Nome exibido no site, app e comunicações.',
};

export const descriptionField: FieldDef = {
  key: 'descricao',
  label: 'Descrição da promoção',
  type: 'textarea',
  required: true,
  rows: 3,
  placeholder: 'Máximo 250 caracteres',
  helperText: 'Até 250 caracteres. Exibida no site e app.',
};

export const internalDescriptionField: FieldDef = {
  key: 'descricaoInterna',
  label: 'Descrição interna de campanha',
  type: 'textarea',
  rows: 2,
  helperText: 'Controle interno — não é exibida no site/app.',
};

export const defaultIndustryOptions: NonNullable<FieldDef['options']> = [
  { value: '1', label: '1 — Unilever' },
  { value: '2', label: '2 — Multi' },
  { value: '3', label: '3 — E-UB' },
];

export const createIndustryField = (
  options: NonNullable<FieldDef['options']> = defaultIndustryOptions
): FieldDef => ({
  key: 'industria',
  label: 'Indústria',
  type: 'select',
  required: true,
  options,
  bulkImport: true,
});

export const industryField: FieldDef = createIndustryField();

export const multiSponsorField: FieldDef = {
  key: 'multiPatrocinador',
  label: 'Multi patrocinador',
  type: 'select',
  required: true,
  options: [
    { value: '0', label: '0 — Não' },
    { value: '1', label: '1 — Sim' },
  ],
};

export const sponsorField: FieldDef = {
  key: 'patrocinadores',
  label: 'Patrocinador(es) e contribuição',
  type: 'text',
  placeholder: 'ex: Unilever 100%',
  helperText: 'Lista os patrocinadores e suas contribuições (% ou R$).',
};

export const cumulativeField: FieldDef = {
  key: 'cumulativa',
  label: 'Promoção cumulativa?',
  type: 'select',
  required: true,
  options: [
    { value: '1', label: '1 — Sim' },
    { value: '2', label: '2 — Não' },
  ],
};

export const startDateField: FieldDef = {
  key: 'inicioVigencia',
  label: 'Início da vigência',
  type: 'datetime',
  required: true,
  placeholder: 'dd/mm/yyyy hh:mm',
  helperText: 'Formato: dd/mm/yyyy hh:mm',
};

export const endDateField: FieldDef = {
  key: 'fimVigencia',
  label: 'Fim da vigência',
  type: 'datetime',
  required: true,
  placeholder: 'dd/mm/yyyy hh:mm',
  helperText: 'Formato: dd/mm/yyyy hh:mm',
};

export const budgetLinkedField: FieldDef = {
  key: 'budgetAtrelado',
  label: 'Budget atrelado?',
  type: 'select',
  options: [
    { value: '1', label: '1 — Sim' },
    { value: '2', label: '2 — Não' },
  ],
};

export const budgetValueField: FieldDef = {
  key: 'budgetValor',
  label: 'Budget (R$)',
  type: 'currency',
  placeholder: '0,00',
  helperText: 'Valor do budget, se houver.',
  conditional: { field: 'budgetAtrelado', values: ['1'] },
};

export const sellersIncludedField: FieldDef = {
  key: 'sellersIncluidos',
  label: 'Sellers incluídos',
  type: 'multiselect',
  options: sellerOptions,
  excludeValuesFrom: 'sellersExcecao',
  bulkImport: true,
  helperText: 'Selecione os sellers que terão acesso à promoção.',
};

export const sellersExceptionField: FieldDef = {
  key: 'sellersExcecao',
  label: 'Sellers exceção',
  type: 'multiselect',
  options: sellerOptions,
  excludeValuesFrom: 'sellersIncluidos',
  bulkImport: true,
  helperText: 'Sellers excluídos da promoção (exceção).',
};

export const cnpjsIncludedField: FieldDef = {
  key: 'cnpjsIncluidos',
  label: 'CNPJs incluídos (varejistas)',
  type: 'tags',
  placeholder: 'Digite ou cole CNPJs',
  excludeValuesFrom: 'cnpjsExcecao',
  bulkImport: true,
  helperText: 'Informe os CNPJs que terão direito à promoção.',
};

export const cnpjsExceptionField: FieldDef = {
  key: 'cnpjsExcecao',
  label: 'CNPJs excluídos (varejistas)',
  type: 'tags',
  placeholder: 'Digite ou cole CNPJs',
  excludeValuesFrom: 'cnpjsIncluidos',
  bulkImport: true,
  helperText: 'Informe os CNPJs excluídos da promoção.',
};

export const channelsField: FieldDef = {
  key: 'canais',
  label: 'Canais de veiculação de promoção',
  type: 'multiselect',
  required: true,
  options: [
    { value: '1', label: '1 — Site' },
    { value: '2', label: '2 — App' },
    { value: '3', label: '3 — WhatsApp' },
  ],
};

export const orderTypeField: FieldDef = {
  key: 'tipoPedido',
  label: 'Tipo de pedido',
  type: 'multiselect',
  required: true,
  options: [
    { value: '1', label: '1 — Autônomo' },
    { value: '2', label: '2 — Venda assistida' },
  ],
};

export const discountCriteriaField: FieldDef = {
  key: 'criterioDesconto',
  label: 'Critério de desconto',
  type: 'select',
  required: true,
  options: [
    { value: '1', label: '1 — Quantidade mín/máx' },
    { value: '2', label: '2 — Valor mín/máx' },
  ],
  unique: true,
};

export const minQtyField: FieldDef = {
  key: 'qtdMinima',
  label: 'Quantidade mínima',
  type: 'number',
  placeholder: 'ex: 8',
  helperText: 'Preencher apenas se Critério de desconto = 1. Em unidades.',
  conditional: { field: 'criterioDesconto', values: ['1'] },
  unique: true,
};

export const maxQtyField: FieldDef = {
  key: 'qtdMaxima',
  label: 'Quantidade máxima',
  type: 'number',
  placeholder: 'Vazio = ilimitado',
  helperText: 'Preencher apenas se Critério = 1 e houver limite. Deixar vazio se ilimitado.',
  conditional: { field: 'criterioDesconto', values: ['1'] },
  unique: true,
};

export const minValueField: FieldDef = {
  key: 'valorMinimo',
  label: 'Valor mínimo (R$)',
  type: 'currency',
  placeholder: '0,00',
  helperText: 'Preencher apenas se Critério de desconto = 2.',
  conditional: { field: 'criterioDesconto', values: ['2'] },
  unique: true,
};

export const maxValueField: FieldDef = {
  key: 'valorMaximo',
  label: 'Valor máximo (R$)',
  type: 'currency',
  placeholder: 'Vazio = ilimitado',
  helperText: 'Preencher apenas se Critério = 2 e houver limite. Deixar vazio se ilimitado.',
  conditional: { field: 'criterioDesconto', values: ['2'] },
  unique: true,
};

export const itemTypeIncludedField: FieldDef = {
  key: 'tipoItemIncluido',
  label: 'Tipo de item a ser INCLUÍDO na promoção',
  type: 'select',
  required: true,
  options: [
    { value: '1', label: '1 — EANs' },
    { value: '2', label: '2 — SKUs' },
    { value: '3', label: '3 — Marcas' },
    { value: '4', label: '4 — Categorias' },
    { value: '5', label: '5 — Subcategorias' },
    { value: '6', label: '6 — Departamentos' },
    { value: '7', label: '7 — Indústrias' },
    { value: '8', label: '8 — Famílias' },
  ],
  unique: true,
};

export const itemsIncludedField: FieldDef = {
  key: 'itensIncluidos',
  label: 'Itens a serem INCLUÍDOS na promoção',
  type: 'tags',
  required: true,
  placeholder: 'Digite e pressione Enter para adicionar',
  helperText: 'Preencher conforme o tipo escolhido no campo anterior.',
  unique: true,
};

export const mandatoryEANsField: FieldDef = {
  key: 'eansObrigatorios',
  label: 'EANs obrigatórios',
  type: 'tags',
  placeholder: 'Digite, cole ou importe EANs',
  helperText: 'EANs obrigatórios para ativar a promoção.',
  unique: true,
  bulkImport: true,
};

export const mandatorySkusField: FieldDef = {
  key: 'skusObrigatorios',
  label: 'SKUs obrigatórios',
  type: 'tags',
  placeholder: 'Digite, cole ou importe SKUs',
  helperText: 'SKUs obrigatórios para ativar a promoção.',
  unique: true,
  bulkImport: true,
};

export const skuComboField: FieldDef = {
  key: 'skuCombo',
  label: 'SKU é combo?',
  type: 'select',
  options: [
    { value: '0', label: '0 — Não' },
    { value: '1', label: '1 — Sim' },
  ],
  helperText: 'Preencher quando houver SKU. Use 0 — Não quando não se aplicar.',
  unique: true,
};

export const familyIndustryField: FieldDef = {
  key: 'industriaFamilia',
  label: 'Indústria da família',
  type: 'text',
  placeholder: 'ex: Loreal',
  helperText: 'Apenas uma indústria por família.',
};

const cardChildOptions = Array.from({ length: 15 }, (_, index) => {
  const value = String(index + 2);
  return { value, label: `${value} — Card filho ${value}` };
});

export const cardNumberField: FieldDef = {
  key: 'numeroCard',
  label: 'Número do card na promo',
  type: 'select',
  required: true,
  options: [
    { value: '0', label: '0 — Card único' },
    { value: '1', label: '1 — Card pai/principal' },
    ...cardChildOptions,
  ],
  unique: true,
};

export const discountValueField: FieldDef = {
  key: 'valorDesconto',
  label: 'Valor do desconto (R$ ou %)',
  type: 'number',
  required: true,
  placeholder: 'ex: 7',
  helperText: 'Valor numérico puro. Não incluir "%" ou "R$".',
  unique: true,
};

export const discountTypeField: FieldDef = {
  key: 'tipoDesconto',
  label: 'Tipo de desconto promocional',
  type: 'select',
  required: true,
  options: [
    { value: '1', label: '1 — By_percent (% no item)' },
    { value: '2', label: '2 — Cart_fixed (R$ no carrinho)' },
    { value: '3', label: '3 — By_fixed (R$ no item)' },
  ],
  unique: true,
};

export const buildBasicInfoSection = (mechanicValue: string): SectionDef => ({
  title: 'Informações Básicas',
  fields: [
    requestTypeField,
    idIFCField,
    { ...mechanicTypeField(mechanicValue) },
    promoNameField,
    displayNameField,
    descriptionField,
    internalDescriptionField,
  ],
});

export const buildFinancialSection = (
  includeDiscountType = true,
  includeDiscountValue = true,
  industryOptions: NonNullable<FieldDef['options']> = defaultIndustryOptions
): SectionDef => ({
  title: 'Desconto e Patrocínio',
  fields: [
    ...(includeDiscountType ? [discountTypeField] : []),
    ...(includeDiscountValue ? [discountValueField] : []),
    createIndustryField(industryOptions),
    multiSponsorField,
    sponsorField,
    cumulativeField,
  ],
});

export const buildValiditySection = (): SectionDef => ({
  title: 'Vigência e Budget',
  fields: [startDateField, endDateField, budgetLinkedField, budgetValueField],
});

export const buildTargetingSection = (): SectionDef => ({
  title: 'Segmentação',
  fields: [
    sellersIncludedField,
    sellersExceptionField,
    cnpjsIncludedField,
    cnpjsExceptionField,
    channelsField,
    orderTypeField,
  ],
});

export const buildProductsSection = (): SectionDef => ({
  title: 'Produtos',
  fields: [
    discountCriteriaField,
    minQtyField,
    maxQtyField,
    minValueField,
    maxValueField,
    itemTypeIncludedField,
    itemsIncludedField,
    mandatoryEANsField,
    mandatorySkusField,
    skuComboField,
    familyIndustryField,
  ],
});
